import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import { useNavigation } from "../contexts/NavigationContext";

export const GmailCallback: React.FC = () => {
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing",
  );
  const [message, setMessage] = useState("Connecting to Gmail...");
  const { navigate } = useNavigation();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const error = urlParams.get("error");

        const isPopup = window.opener && !window.opener.closed;

        if (error) {
          setStatus("error");

          // Check for specific Google OAuth errors
          let errorMessage = `Authorization failed: ${error}`;
          const errorDescription = urlParams.get("error_description");

          if (
            error === "access_denied" &&
            errorDescription?.includes("verification")
          ) {
            errorMessage =
              "Gmail connection blocked: Your Google OAuth app is in Testing mode and your email is not added as a test user. Please add your email as a test user in Google Cloud Console (APIs & Services > OAuth consent screen > Test users) or submit your app for verification.";
          } else if (error === "access_denied") {
            errorMessage =
              "Gmail connection blocked: Access was denied. This may be because your Google OAuth app is in Testing mode. Please add your email as a test user in Google Cloud Console.";
          } else if (errorDescription) {
            errorMessage = `Authorization failed: ${errorDescription}`;
          }

          setMessage(errorMessage);

          if (isPopup) {
            window.opener.postMessage(
              {
                type: "GMAIL_AUTH_ERROR",
                message: errorMessage,
              },
              window.location.origin,
            );
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        if (!code) {
          setStatus("error");
          setMessage("No authorization code received from Gmail");

          if (isPopup) {
            window.opener.postMessage(
              {
                type: "GMAIL_AUTH_ERROR",
                message: "No authorization code received",
              },
              window.location.origin,
            );
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        setMessage("Exchanging authorization code for tokens...");

        const response = await fetch(
          `${apiUrl}/api/gmail/callback${window.location.search}`,
          {
            method: "GET",
          },
        );

        const data = await response.json();

        if (!data.success) {
          setStatus("error");
          setMessage(data.message || "Failed to complete Gmail authorization");

          if (isPopup) {
            window.opener.postMessage(
              {
                type: "GMAIL_AUTH_ERROR",
                message: data.message || "Failed to complete authorization",
              },
              window.location.origin,
            );
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        setMessage("Saving connection details...");

        const token = localStorage.getItem("token");
        const completeResponse = await fetch(
          `${apiUrl}/api/gmail/complete-connection`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data.data),
          },
        );

        const completeData = await completeResponse.json();

        // Check if this is a limit error (403 with integration limit)
        if (
          completeResponse.status === 403 &&
          !completeData.success &&
          completeData.limit
        ) {
          setStatus("error");
          setMessage("Integration limit reached. Please upgrade to Premium.");

          if (isPopup) {
            // Send limit error to parent window so it can open upgrade modal
            window.opener.postMessage(
              {
                type: "GMAIL_LIMIT_ERROR",
                limit: completeData.limit,
              },
              window.location.origin,
            );
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        if (completeData.success) {
          setStatus("success");
          setMessage("Gmail connected successfully!");

          if (isPopup) {
            window.opener.postMessage(
              {
                type: "GMAIL_AUTH_SUCCESS",
              },
              window.location.origin,
            );

            setTimeout(() => window.close(), 1000);
          } else {
            setTimeout(() => {
              navigate("/admin");
            }, 2000);
          }
        } else {
          setStatus("error");
          setMessage(completeData.message || "Failed to save Gmail connection");

          if (isPopup) {
            window.opener.postMessage(
              {
                type: "GMAIL_AUTH_ERROR",
                message: completeData.message || "Failed to save connection",
              },
              window.location.origin,
            );
            setTimeout(() => window.close(), 2000);
          }
        }
      } catch (error) {
        console.error("Gmail callback error:", error);
        setStatus("error");
        setMessage("An unexpected error occurred while connecting to Gmail");

        const isPopup = window.opener && !window.opener.closed;
        if (isPopup) {
          window.opener.postMessage(
            {
              type: "GMAIL_AUTH_ERROR",
              message: "An unexpected error occurred",
            },
            window.location.origin,
          );
          setTimeout(() => window.close(), 2000);
        }
      }
    };

    handleCallback();
  }, [apiUrl, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === "processing" && (
            <>
              <Loader className="w-16 h-16 text-primary-500 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Connecting to Gmail
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Connection Successful!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                Redirecting to settings...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              <button
                onClick={() => navigate("/admin")}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Return to Settings
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
